import {inject, Injectable} from '@angular/core';
import {Observable, map} from 'rxjs';
import {
  GeneratedUseCaseAdminResponse,
  GeneratedUseCaseEnabledUpdateRequest,
  GeneratedUseCaseImproveRequest as ApiGeneratedUseCaseImproveRequest,
  GeneratedUseCaseUpdateRequest as ApiGeneratedUseCaseUpdateRequest,
  GlobalUseCaseGenerationControllerService,
  GlobalUseCaseGenerationRequest as ApiGlobalUseCaseGenerationRequest,
  GlobalUseCaseGenerationResponse as ApiGlobalUseCaseGenerationResponse,
  RunModuleFunctionAttributesResponse,
  UseCaseGenerationRunAdminResponse as ApiUseCaseGenerationRunAdminResponse,
  UseCaseGenerationRunStatusResponse as ApiUseCaseGenerationRunStatusResponse,
} from '@frontend/shared-api';

export interface GeneratedUseCaseAdminRow {
  id: number;
  runId: number | null;
  agendaItemId: number;
  agendaItemSubCategoryId: string;
  moduleFunctionId: number;
  personaId: string;
  titleDe: string;
  descriptionDe: string;
  enabled: boolean;
  durationMin: number;
  sortOrder: number;
  subcriteriaAttributeIds: string[];
  createdAt: string;
}

export interface GlobalUseCaseGenerationResponse {
  runId: number;
  status: string;
  startedAt: string;
  finishedAt: string | null;
  createdCount: number;
  skippedDuplicateCount: number;
  errorCount: number;
  processedModuleFunctionIds: number[];
  dryRun: boolean;
}

export interface UseCaseGenerationRunStatusResponse {
  running: boolean;
  runId: number | null;
  status: string | null;
  startedAt: string | null;
}

export interface UseCaseGenerationRunAdminRow {
  id: number;
  status: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdCount: number;
  skippedDuplicateCount: number;
  errorCount: number;
  errorMessage: string | null;
  dryRun: boolean;
  usedModuleFunctionIds: number[];
  usedAttributesByModuleFunction: {moduleFunctionId: number; attributeIds: string[]}[];
  usedAgendaItemIds: number[];
}

export interface GlobalUseCaseGenerationRequest {
  moduleFunctionIds?: number[];
  limit?: number;
  dryRun?: boolean;
}

export interface GeneratedUseCaseUpdateRequest {
  titleDe: string;
  descriptionDe: string;
  durationMin: number;
}

export interface GeneratedUseCaseImproveResponse {
  text: string;
}

@Injectable({providedIn: 'root'})
export class SelecthorAdminUseCasesService {
  private readonly api = inject(GlobalUseCaseGenerationControllerService);

  loadUseCases(filters?: {
    agendaItemId?: number | null;
    moduleFunctionId?: number | null;
    personaId?: string | null;
  }): Observable<GeneratedUseCaseAdminRow[]> {
    return this.api
      .loadGeneratedUseCases({
        agendaItemId: filters?.agendaItemId ?? undefined,
        moduleFunctionId: filters?.moduleFunctionId ?? undefined,
        personaId: filters?.personaId ?? undefined,
      })
      .pipe(map((rows) => (rows ?? []).map((row) => this.toGeneratedUseCaseRow(row))));
  }

  startGenerationRun(
    request?: GlobalUseCaseGenerationRequest,
  ): Observable<GlobalUseCaseGenerationResponse> {
    return this.api
      .generateUseCases({
        globalUseCaseGenerationRequest: (request ?? {}) as ApiGlobalUseCaseGenerationRequest,
      })
      .pipe(map((response) => this.toGenerationResponse(response)));
  }

  loadGenerationStatus(): Observable<UseCaseGenerationRunStatusResponse> {
    return this.api.getGenerationStatus().pipe(map((status) => this.toGenerationStatus(status)));
  }

  loadGenerationRuns(): Observable<UseCaseGenerationRunAdminRow[]> {
    return this.api
      .loadGenerationRuns()
      .pipe(map((runs) => (runs ?? []).map((run) => this.toRunRow(run))));
  }

  updateEnabled(useCaseId: number, enabled: boolean): Observable<GeneratedUseCaseAdminRow> {
    const request: GeneratedUseCaseEnabledUpdateRequest = {enabled};
    return this.api
      .updateEnabled({
        id: useCaseId,
        generatedUseCaseEnabledUpdateRequest: request,
      })
      .pipe(map((row) => this.toGeneratedUseCaseRow(row)));
  }

  updateUseCase(
    useCaseId: number,
    request: GeneratedUseCaseUpdateRequest,
  ): Observable<GeneratedUseCaseAdminRow> {
    return this.api
      .updateUseCase({
        id: useCaseId,
        generatedUseCaseUpdateRequest: request as ApiGeneratedUseCaseUpdateRequest,
      })
      .pipe(map((row) => this.toGeneratedUseCaseRow(row)));
  }

  improveTitle(text: string): Observable<GeneratedUseCaseImproveResponse> {
    const request: ApiGeneratedUseCaseImproveRequest = {text};
    return this.api.improveTitle({generatedUseCaseImproveRequest: request}).pipe(
      map((response) => ({
        text: response.text ?? '',
      })),
    );
  }

  improveDescription(text: string): Observable<GeneratedUseCaseImproveResponse> {
    const request: ApiGeneratedUseCaseImproveRequest = {text};
    return this.api.improveDescription({generatedUseCaseImproveRequest: request}).pipe(
      map((response) => ({
        text: response.text ?? '',
      })),
    );
  }

  private toGeneratedUseCaseRow(row: GeneratedUseCaseAdminResponse): GeneratedUseCaseAdminRow {
    return {
      id: row.id ?? 0,
      runId: row.runId ?? null,
      agendaItemId: row.agendaItemId ?? 0,
      agendaItemSubCategoryId: row.agendaItemSubCategoryId ?? '',
      moduleFunctionId: row.moduleFunctionId ?? 0,
      personaId: row.personaId ?? '',
      titleDe: row.titleDe ?? '',
      descriptionDe: row.descriptionDe ?? '',
      enabled: Boolean(row.enabled),
      durationMin: row.durationMin ?? 0,
      sortOrder: row.sortOrder ?? 0,
      subcriteriaAttributeIds: row.subcriteriaAttributeIds ?? [],
      createdAt: row.createdAt ?? '',
    };
  }

  private toGenerationResponse(
    response: ApiGlobalUseCaseGenerationResponse,
  ): GlobalUseCaseGenerationResponse {
    return {
      runId: response.runId ?? 0,
      status: response.status ?? '',
      startedAt: response.startedAt ?? '',
      finishedAt: response.finishedAt ?? null,
      createdCount: response.createdCount ?? 0,
      skippedDuplicateCount: response.skippedDuplicateCount ?? 0,
      errorCount: response.errorCount ?? 0,
      processedModuleFunctionIds: response.processedModuleFunctionIds ?? [],
      dryRun: Boolean(response.dryRun),
    };
  }

  private toGenerationStatus(
    status: ApiUseCaseGenerationRunStatusResponse,
  ): UseCaseGenerationRunStatusResponse {
    return {
      running: Boolean(status.running),
      runId: status.runId ?? null,
      status: status.status ?? null,
      startedAt: status.startedAt ?? null,
    };
  }

  private toRunRow(run: ApiUseCaseGenerationRunAdminResponse): UseCaseGenerationRunAdminRow {
    return {
      id: run.id ?? 0,
      status: run.status ?? null,
      startedAt: run.startedAt ?? null,
      finishedAt: run.finishedAt ?? null,
      createdCount: run.createdCount ?? 0,
      skippedDuplicateCount: run.skippedDuplicateCount ?? 0,
      errorCount: run.errorCount ?? 0,
      errorMessage: run.errorMessage ?? null,
      dryRun: Boolean(run.dryRun),
      usedModuleFunctionIds: run.usedModuleFunctionIds ?? [],
      usedAttributesByModuleFunction: (run.usedAttributesByModuleFunction ?? []).map((group) =>
        this.toRunModuleFunctionAttributes(group),
      ),
      usedAgendaItemIds: run.usedAgendaItemIds ?? [],
    };
  }

  private toRunModuleFunctionAttributes(group: RunModuleFunctionAttributesResponse): {
    moduleFunctionId: number;
    attributeIds: string[];
  } {
    return {
      moduleFunctionId: group.moduleFunctionId ?? 0,
      attributeIds: group.attributeIds ?? [],
    };
  }
}
